// Phase 5 · Compliance / Staff Certifications client library.
//
// Two surfaces:
//   1. Admin — full CRUD on cert_types + staff_certifications, plus
//      the compliance overview that powers /admin/compliance KPIs.
//   2. Teacher — read-only "my certifications + upcoming renewals".

import { supabase, isSupabaseConfigured } from "@/lib/supabase";

const BUCKET = "staff-certifications";
const SIGNED_URL_TTL = 60 * 60; // 1 hour

const TYPE_SELECT = `
  id, organization_id, code, label, description,
  default_validity_months, is_required,
  blocks_attendance_when_missing,
  sort_order, active,
  created_at, updated_at
`;
const CERT_SELECT = `
  id, organization_id, profile_id, cert_type_id,
  issued_on, expires_on, document_path, notes,
  revoked_at, revoked_by, revoke_reason,
  created_at, updated_at,
  cert_type:cert_types(id, code, label, is_required, default_validity_months),
  profile:profiles!staff_certifications_profile_id_fkey(id, full_name, email)
`;

// ---------- cert_types (catalog) ----------

export async function listCertTypes(organizationId, { includeInactive = false } = {}) {
  if (!isSupabaseConfigured || !organizationId) return [];
  let q = supabase
    .from("cert_types")
    .select(TYPE_SELECT)
    .eq("organization_id", organizationId)
    .order("sort_order", { ascending: true });
  if (!includeInactive) q = q.eq("active", true);
  const { data, error } = await q;
  if (error) {
    if (error.code === "42P01") return [];
    throw error;
  }
  return data || [];
}

export async function upsertCertType({
  id, organizationId, code, label, description,
  defaultValidityMonths, isRequired, sortOrder, active,
  blocksAttendanceWhenMissing,
}) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const payload = {
    organization_id: organizationId,
    code: code?.trim(),
    label: label?.trim(),
    description: description?.trim() || null,
    default_validity_months: defaultValidityMonths || null,
    is_required: !!isRequired,
    sort_order: sortOrder ?? 100,
    active: active ?? true,
  };
  if (blocksAttendanceWhenMissing !== undefined) {
    payload.blocks_attendance_when_missing = !!blocksAttendanceWhenMissing;
  }
  if (id) {
    const { data, error } = await supabase
      .from("cert_types").update(payload).eq("id", id).select(TYPE_SELECT).single();
    if (error) throw error;
    return data;
  }
  const { data, error } = await supabase
    .from("cert_types").insert(payload).select(TYPE_SELECT).single();
  if (error) throw error;
  return data;
}

export async function deleteCertType(id) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { error } = await supabase.from("cert_types").delete().eq("id", id);
  if (error) {
    if (error.code === "23503") throw new Error("Cert type is in use — deactivate it instead.");
    throw error;
  }
}

// ---------- staff_certifications ----------

export async function listAllCertificationsForOrg(organizationId) {
  if (!isSupabaseConfigured || !organizationId) return [];
  const { data, error } = await supabase
    .from("staff_certifications")
    .select(CERT_SELECT)
    .eq("organization_id", organizationId)
    .order("expires_on", { ascending: true });
  if (error) {
    if (error.code === "42P01") return [];
    throw error;
  }
  return data || [];
}

export async function listCertificationsForProfile(profileId) {
  if (!isSupabaseConfigured || !profileId) return [];
  const { data, error } = await supabase
    .from("staff_certifications")
    .select(CERT_SELECT)
    .eq("profile_id", profileId)
    .order("expires_on", { ascending: true });
  if (error) {
    if (error.code === "42P01") return [];
    throw error;
  }
  return data || [];
}

export async function upsertCertification({
  id, profileId, certTypeId, issuedOn, expiresOn, documentPath, notes,
}) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase.rpc("rpc_upsert_staff_certification", {
    p_id:             id || null,
    p_profile_id:     profileId,
    p_cert_type_id:   certTypeId,
    p_issued_on:      issuedOn,
    p_expires_on:     expiresOn || null,
    p_document_path:  documentPath || null,
    p_notes:          notes || null,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] || null : data;
}

export async function revokeCertification(id, reason) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase.rpc("rpc_revoke_staff_certification", {
    p_id:     id,
    p_reason: reason || null,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] || null : data;
}

// ---------- documents ----------

export async function uploadCertDocument({ certificationId, file }) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  if (!file) throw new Error("Pick a file");
  const ext = (file.name.split(".").pop() || "bin").toLowerCase().slice(0, 10);
  const path = `${certificationId}/${crypto.randomUUID()}.${ext}`;
  const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, {
    cacheControl: "3600",
    upsert: false,
  });
  if (upErr) throw upErr;
  // Stamp document_path onto the cert row (admin RLS allows this).
  const { error: updErr } = await supabase
    .from("staff_certifications")
    .update({ document_path: path })
    .eq("id", certificationId);
  if (updErr) {
    // Best-effort cleanup so we don't leave orphaned bytes.
    await supabase.storage.from(BUCKET).remove([path]).catch(() => {});
    throw updErr;
  }
  return path;
}

export async function getCertDocumentUrl(path) {
  if (!isSupabaseConfigured || !path) return null;
  const { data, error } = await supabase
    .storage.from(BUCKET).createSignedUrl(path, SIGNED_URL_TTL);
  if (error) return null;
  return data?.signedUrl || null;
}

export async function deleteCertDocument({ certificationId, path }) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  if (!path) return;
  await supabase.storage.from(BUCKET).remove([path]).catch(() => {});
  const { error } = await supabase
    .from("staff_certifications")
    .update({ document_path: null })
    .eq("id", certificationId);
  if (error) throw error;
}

// ---------- helpers ----------

export const STATUS_META = {
  valid:         { label: "Valid",         tone: "border-emerald-200 bg-emerald-50 text-emerald-800" },
  expiring_soon: { label: "Expiring soon", tone: "border-amber-300 bg-amber-50 text-amber-900" },
  expired:       { label: "Expired",       tone: "border-[#C6851E]/40 bg-[#C6851E]/10 text-[#8A5A12]" },
  revoked:       { label: "Revoked",       tone: "border-neutral-300 bg-neutral-100 text-neutral-700" },
};

export function deriveStatus(cert) {
  if (cert.revoked_at) return "revoked";
  if (!cert.expires_on) return "valid";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exp = new Date(`${cert.expires_on}T00:00:00`);
  if (exp <= today) return "expired";
  const thirtyDaysFromNow = new Date(today);
  thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
  if (exp <= thirtyDaysFromNow) return "expiring_soon";
  return "valid";
}

export function daysUntilExpiry(expiresOn) {
  if (!expiresOn) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exp = new Date(`${expiresOn}T00:00:00`);
  return Math.round((exp - today) / (1000 * 60 * 60 * 24));
}

export function suggestExpiryDate(issuedOn, defaultValidityMonths) {
  if (!issuedOn || !defaultValidityMonths) return "";
  const d = new Date(`${issuedOn}T00:00:00`);
  d.setMonth(d.getMonth() + defaultValidityMonths);
  return d.toISOString().slice(0, 10);
}

/** Group cert rows by profile_id and roll up status counts for the admin overview. */
export function rollupByProfile(certs) {
  const map = new Map();
  for (const c of certs) {
    const key = c.profile_id;
    if (!map.has(key)) {
      map.set(key, {
        profile: c.profile,
        certs: [],
        counts: { valid: 0, expiring_soon: 0, expired: 0, revoked: 0 },
        worst: "valid",
      });
    }
    const entry = map.get(key);
    const s = deriveStatus(c);
    entry.certs.push({ ...c, status: s });
    entry.counts[s]++;
  }
  // Worst status precedence: expired > expiring_soon > revoked > valid.
  const order = { expired: 3, expiring_soon: 2, revoked: 1, valid: 0 };
  for (const e of map.values()) {
    e.worst = e.certs.reduce(
      (acc, c) => (order[c.status] > order[acc] ? c.status : acc),
      "valid",
    );
  }
  return Array.from(map.values()).sort((a, b) => order[b.worst] - order[a.worst]);
}
